import {useState, FC, memo, useEffect} from 'react';
import Dialog from '@jobseeker/lotus/components/Dialog';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {Box, Typography} from '@mui/material';
import ButtonV2 from '@jobseeker/lotus/elements/ButtonV2';
import Button from '@jobseeker/lotus/elements/Button';
import {useIsLargeDevice} from './common';
import {handleTracking} from '@jobseeker/common-core/utils';
import {useWrappedTheme} from '@jobseeker/lotus/hooks';
import dynamic from 'next/dynamic';

const BottomSheet = dynamic(
  () => import('@jobseeker/lotus/elements/BottomSheet'),
  {ssr: false}
);

export interface useAlertCallbackInterface {
  primaryAction?: string;
  secondaryAction?: string;
  title?: string | any;
  subtitle?: string | any;
  BodyContent?: FC<any>;
  primaryCallback: any;
  secondaryCallback?: any;
  onClose?: () => void;
  useBottomSheet?: boolean;
  isPrimaryActionDisabled?: boolean;
  isSecondaryActionDisabled?: boolean;
  hideActions?: boolean;
  alert?: boolean;
  forHirist?: boolean;
}
export const useAlertCallback = ({
  primaryAction,
  isPrimaryActionDisabled = false,
  isSecondaryActionDisabled = false,
  secondaryAction,
  // title,
  // subtitle,
  BodyContent,
  primaryCallback,
  secondaryCallback,
  onClose,
  useBottomSheet,
  hideActions = false,
  forHirist = false,
  ...props
}: useAlertCallbackInterface): [
  any,
  () => void,
  // (obj: {title: string | any; subtitle: string | any}) => void,
] => {
  const theme = useWrappedTheme();
  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';
  const [open, setOpen] = useState(false);
  const [callbackParams, setCallbackParams] = useState<any>([]);
  const isLarge = useIsLargeDevice();
  const toggleDialog = (...args: any[]) => {
    setOpen(open => !open);
    setCallbackParams(args);
  };
  const handlePrimaryAction = () => {
    setOpen(false);
    primaryCallback(...callbackParams);

    if (typeof window === 'undefined') {
      return;
    }

    const {location} = window;
    const queryParams = new URLSearchParams(location.href);

    switch (primaryAction) {
      case 'Restart':
        // Send jsRestartRecording GA event
        handleTracking('jsRestartRecording', {
          event_category: 'Round Zero',
          Origin: location?.pathname ?? '',
          Source: 'Resume',
          UserId: sessionStorage.getItem('userId') || 'NA',
          Device: sessionStorage.getItem('device') || 'desktop',
          ref: queryParams.get('ref') ?? '',
        });

        break;

      case '':
        break;
    }
  };
  const handleSecondaryAction = () => {
    if (secondaryCallback) {
      secondaryCallback(...callbackParams);
    }
    setOpen(false);
  };
  const AlertDialog: FC = memo(({subtitle, title, ...dialogProps}: any) => {
    const alertStyles = {boxShadow: 'none', border: 'none'};
    if (useBottomSheet && !isLarge) {
      return (
        <BottomSheet
          title={title || props.title}
          onDismiss={toggleDialog}
          open={open}
          bannerMode={false}
          snapPoints={({minHeight, maxHeight}) => [
            maxHeight < minHeight ? maxHeight : minHeight,
          ]}
          titleExtension={true}
          // skipInitialTransition={true}
          headerStyles={{fontSize: 16, ...alertStyles}}
          {...dialogProps}
        >
          <Box px={2} pb={2}>
            {(subtitle || props.subtitle) && (
              <Typography variant="body2" color="text.secondary">
                {subtitle || props.subtitle}
              </Typography>
            )}

            {BodyContent && <BodyContent />}

            {!hideActions && (
              <Box display={'flex'} gap={1.5} mt={2} mb={2}>
                {primaryAction && (
                  <ButtonV2
                    onClick={handlePrimaryAction}
                    color={isAppIimjobs ? 'secondary' : 'primary'}
                    variant="contained"
                    disabled={isPrimaryActionDisabled}
                    sx={isAppIimjobs ? {} : {borderRadius: '3px'}}
                  >
                    {primaryAction}
                  </ButtonV2>
                )}
                {secondaryAction && (
                  <ButtonV2
                    onClick={handleSecondaryAction}
                    color="primary"
                    variant="outlined"
                    disabled={isSecondaryActionDisabled}
                  >
                    {secondaryAction}
                  </ButtonV2>
                )}
              </Box>
            )}
          </Box>
        </BottomSheet>
      );
    } else {
      return (
        <Dialog
          title={title || props.title}
          variant={VersionVariants.VERSION_THREE}
          open={open}
          onClose={onClose ? onClose : toggleDialog}
          fullScreen={false}
          sx={{
            '& .MuiDialog-paper': {
              minWidth: {xxs: 'calc(100vw - 16px)', md: 'fit-content'},
              ...(forHirist && {borderRadius: '8px'}),
            },
          }}
          titleProps={{
            pt: 3,
            px: {xxs: 2, md: 3},
          }}
          disableCrossButton={props.alert}
          {...dialogProps}
        >
          <Box
            px={{xxs: 1, md: 2}}
            sx={{
              minWidth: {xxs: 'unset', md: forHirist ? 560 : 460},
              maxWidth: {xxs: 'unset', md: forHirist ? 560 : 460},
            }}
          >
            {subtitle ||
              (props.subtitle && (
                <Typography
                  variant="subtitle2"
                  color={'text.secondary'}
                  sx={{
                    ...(forHirist && {
                      fontSize: {xxs: 16, md: 18},
                      fontWeight: 200,
                      color: '#000',
                    }),
                  }}
                >
                  {subtitle || props.subtitle}
                </Typography>
              ))}
            {BodyContent && <BodyContent />}
            {!hideActions && (
              <Box display="flex" gap={1.5} mt={forHirist ? 4.375 : 3} mb={2}>
                {forHirist ? (
                  <>
                    {primaryAction && (
                      <Button
                        sx={{
                          fontSize: '16px',
                          backgroundColor: theme.palette.primary.main,
                          px: 5,
                          py: 1.25,
                          height: 'fit-content',
                          borderRadius: '3px',
                          '&:hover': {
                            backgroundColor: '#e79e00',
                          },
                        }}
                        onClick={handlePrimaryAction}
                        color="secondary"
                        variant="contained"
                        disabled={isPrimaryActionDisabled}
                      >
                        {primaryAction}
                      </Button>
                    )}
                    {secondaryAction && (
                      <Button
                        sx={{
                          fontSize: '16px',

                          height: 'fit-content',
                          color: theme.palette.primary.main,
                        }}
                        onClick={handleSecondaryAction}
                        variant="text"
                        disabled={isSecondaryActionDisabled}
                      >
                        {secondaryAction}
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    {primaryAction && (
                      <ButtonV2
                        onClick={handlePrimaryAction}
                        color={isAppIimjobs ? 'secondary' : 'primary'}
                        variant="contained"
                        disabled={isPrimaryActionDisabled}
                        sx={
                          isAppIimjobs
                            ? {}
                            : {
                                borderRadius: '3px',
                                '&:hover': {
                                  backgroundColor: theme.palette.primary.main,
                                },
                              }
                        }
                      >
                        {primaryAction}
                      </ButtonV2>
                    )}
                    {secondaryAction && (
                      <ButtonV2
                        onClick={handleSecondaryAction}
                        color="primary"
                        sx={isAppIimjobs ? {} : {borderRadius: '3px'}}
                        variant="outlined"
                        disabled={isSecondaryActionDisabled}
                      >
                        {secondaryAction}
                      </ButtonV2>
                    )}
                  </>
                )}
              </Box>
            )}
          </Box>
        </Dialog>
      );
    }
  });

  return [AlertDialog, toggleDialog];
};
